import {AiWorkstreamUpdateSummariesService, type WorkstreamUpdate} from 'client';
import {useEffect, useMemo, useSyncExternalStore} from 'react';
import {getService} from 'utilities';
import {getWorkstreamDescriptionPlainText} from 'views/workstreams/detail/workstreamDescriptionRichText';

/**
 * /workstreams: an update's one-sentence summary. It's stored on the update
 * (`update.summary`), written by AI in the background after the update is
 * posted or edited, or by a person who edits it. Updates viewed before that
 * lands ask POST /api/v1/workstream/update-summaries, which generates and
 * saves it; asks are batched into one call per tick (max BATCH_SIZE). Failures
 * aren't retried — the card keeps showing its excerpt.
 */

// Matches the endpoint's per-request cap.
const BATCH_SIZE = 10;
// Shorter updates already read as a summary — shown as written, no AI.
// Matches SUMMARIZE_MIN_CHARS in helpers/workstream_update_summary.py.
export const SUMMARIZE_MIN_CHARS = 140;

// Summaries used to be cached per browser; they live on the server now.
try {
  Object.keys(localStorage)
    .filter((key) => key.startsWith('workstreams5_update_summaries_'))
    .forEach((key) => localStorage.removeItem(key));
} catch {
  /* storage unavailable */
}

// Summaries fetched this session, until the update itself carries one. Keyed
// by id and text, so an edited update (whose AI summary the server clears)
// doesn't show the old one.
const fetched = new Map<string, string>();
const pending = new Map<number, string>();
const inFlight = new Set<string>();
const requested = new Set<string>();
const listeners = new Set<() => void>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const cacheKey = (update: WorkstreamUpdate) => `${update.id}:${update.comment}`;

const notify = () => listeners.forEach((listener) => listener());

const flush = async () => {
  flushTimer = null;
  const batch = [...pending.entries()].slice(0, BATCH_SIZE);
  batch.forEach(([id]) => pending.delete(id));
  if (pending.size > 0) scheduleFlush();
  try {
    const {summaries} = await getService(
      AiWorkstreamUpdateSummariesService
    ).getUpdateSummariesApiV1WorkstreamUpdateSummariesPost({ids: batch.map(([id]) => id)});
    for (const [id, key] of batch) {
      const summary = summaries[String(id)]?.trim();
      if (summary) fetched.set(key, summary);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.warn('[workstreams4] update summaries request failed', error);
  } finally {
    batch.forEach(([, key]) => inFlight.delete(key));
    notify();
  }
};

const scheduleFlush = () => {
  if (flushTimer === null) flushTimer = setTimeout(() => void flush(), 50);
};

/** Drop what this session fetched for an update, so a reset summary is asked for again. */
export const forgetUpdateSummary = (updateId: number) => {
  const prefix = `${updateId}:`;
  for (const set of [fetched, requested]) {
    for (const key of set.keys()) {
      if (key.startsWith(prefix)) set.delete(key);
    }
  }
  notify();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export type UpdateSummaryState = {
  summary?: string;
  // A person wrote or edited it (no AI sparkle).
  edited: boolean;
  // Being generated right now.
  pending: boolean;
};

/** An update's summary: stored on the update, else requested (batched). */
export const useUpdateSummary = (update?: WorkstreamUpdate): UpdateSummaryState => {
  const stored = update?.summary?.trim() || undefined;
  const summarizable = useMemo(
    () =>
      Boolean(update) &&
      getWorkstreamDescriptionPlainText(update?.comment).trim().length > SUMMARIZE_MIN_CHARS,
    [update]
  );
  const key = update && summarizable && !stored ? cacheKey(update) : undefined;

  const summary = useSyncExternalStore(subscribe, () =>
    key === undefined ? undefined : fetched.get(key)
  );
  const isPending = useSyncExternalStore(subscribe, () =>
    key === undefined ? false : inFlight.has(key)
  );

  useEffect(() => {
    if (!update || key === undefined) return;
    if (fetched.has(key) || requested.has(key)) return;
    requested.add(key);
    inFlight.add(key);
    pending.set(update.id, key);
    notify();
    scheduleFlush();
  }, [update, key]);

  if (stored) return {summary: stored, edited: Boolean(update?.summary_edited), pending: false};
  return {summary, edited: false, pending: !summary && isPending};
};
